// Chiave pubblica VAPID fornita dal server per la sottoscrizione push
const pushServerPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;

/**
 * Verifica se le notifiche push e i service worker sono supportati dal browser.
 * @returns {boolean} true se supportati, false altrimenti
 */
export function isPushNotificationSupported() {
  return (
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

/**
 * Chiede all'utente il consenso a ricevere notifiche push.
 * @returns {Promise<NotificationPermission>} "granted", "default" o "denied"
 * @throws {Error} se le notifiche push non sono supportate
 */
export async function askUserPermission() {
  if (!isPushNotificationSupported()) {
    throw new Error("Push notifications not supported");
  }
  return await Notification.requestPermission();
}

/**
 * Registra un service worker per la gestione delle notifiche push.
 * @param {string | URL} scriptURL - Percorso dello script del service worker
 * @param {RegistrationOptions} [options] - Opzioni di registrazione opzionali
 * @returns {Promise<ServiceWorkerRegistration>} Oggetto di registrazione del service worker
 */
export function registerServiceWorker(
  scriptURL: string | URL,
  options?: RegistrationOptions,
) {
  return navigator.serviceWorker.register(scriptURL, options);
}

/**
 * Crea una sottoscrizione push utilizzando il service worker registrato e la restituisce.
 * @returns {Promise<PushSubscription>} Oggetto di sottoscrizione push
 */
export async function createNotificationSubscription() {
  // Attende che il service worker sia pronto
  const serviceWorker = await navigator.serviceWorker.ready;
  // Crea e restituisce la sottoscrizione push
  return await serviceWorker.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: pushServerPublicKey,
  });
}

/**
 * Restituisce lo stato del permesso per la sottoscrizione push ("granted", "prompt", "denied").
 * @returns {Promise<PermissionState>} Stato del permesso push
 */
export async function getSubscriptionState() {
  const serviceWorker = await navigator.serviceWorker.ready;
  return serviceWorker.pushManager.permissionState({
    userVisibleOnly: true,
    applicationServerKey: pushServerPublicKey,
  });
}

/**
 * Restituisce la sottoscrizione push dell'utente se presente, altrimenti null.
 * @returns {Promise<PushSubscription | null>} Oggetto di sottoscrizione o null
 */
export async function getUserSubscription() {
  const serviceWorker = await navigator.serviceWorker.ready;
  return serviceWorker.pushManager.getSubscription();
}

/**
 * Tipo semplificato per rappresentare una sottoscrizione push.
 */
export type PushSubscription = {
  endpoint: string;
  expirationTime: EpochTimeStamp;
};
